const ONES = ['sıfır', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz'];
const TENS = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan'];
const MONTHS = [
  'ocak',
  'şubat',
  'mart',
  'nisan',
  'mayıs',
  'haziran',
  'temmuz',
  'ağustos',
  'eylül',
  'ekim',
  'kasım',
  'aralık',
];

const NUMERIC_DATE =
  /(?<![\p{L}\p{N}./-])(\d{1,2})([./-])(\d{1,2})\2([1-9]\d{3})(?!\d|[./-]\d)(?:\s+([01]\d|2[0-3]):([0-5]\d)(?!\d|:\d))?/gu;

function words(value: number): string {
  return value < 10
    ? ONES[value]!
    : `${TENS[Math.floor(value / 10)]} ${value % 10 ? ONES[value % 10] : ''}`.trim();
}

function numberForms(max: number, padded = false): Map<string, number> {
  const forms = new Map<string, number>();
  for (let value = 0; value <= max; value++) {
    forms.set(words(value), value);
    forms.set(String(value), value);
    if (padded && value < 10) {
      forms.set(`sıfır ${words(value)}`, value);
      forms.set(`0${value}`, value);
    }
  }
  return forms;
}

function pattern(forms: Iterable<string>): string {
  return [...forms]
    .sort((a, b) => b.length - a.length)
    .join('|')
    .replace(/ı/g, '[ıI]')
    .replace(/i/g, '[iİ]')
    .replace(/ /g, '\\s+');
}

function lookup(forms: Map<string, number>, text: string): number {
  return forms.get(text.toLocaleLowerCase('tr-TR').replace(/\s+/g, ' '))!;
}

const HOURS = numberForms(23, true);
const MINUTES = numberForms(59, true);
// A single unpadded digit is ambiguous with a compound hour such as "on beş".
for (let value = 0; value < 10; value++) {
  MINUTES.delete(words(value));
  MINUTES.delete(String(value));
}
MINUTES.set('buçuk', 30);
const SUFFIX = "(?:['’]?[dt][ae])";
const NUMBER_CONTINUATION = `(?:${pattern([...ONES, ...TENS.filter(Boolean), 'yüz', 'bin'])}|\\d+)(?![\\p{L}\\p{N}])`;
const FOLLOWING_NUMBER = new RegExp(`^\\s+${NUMBER_CONTINUATION}`, 'iu');
const TIME = new RegExp(
  `(?<![\\p{L}\\p{N}])((?:(sabah|öğleden sonra|akşam)\\s+)?saat\\s*:?\\s+)(${pattern(HOURS.keys())})(?:(?:\\s+|[.:])(${pattern(MINUTES.keys())})(${SUFFIX})?|(${SUFFIX}))(?![\\p{L}\\p{N}]|[.:]\\d|\\s+(?:dakika|saniye|saat)(?!\\p{L}))`,
  'giu'
);

const DAYS = numberForms(31);
DAYS.delete('0');
DAYS.delete('sıfır');
const YEARS = new Map<string, number>();
for (let year = 1900; year <= 2099; year++) {
  YEARS.set(String(year), year);
  YEARS.set(
    `${year < 2000 ? 'bin dokuz yüz' : 'iki bin'}${year % 100 ? ` ${words(year % 100)}` : ''}`,
    year
  );
}
const DATE = new RegExp(
  `(?<![\\p{L}\\p{N}])(${pattern(DAYS.keys())})\\s+(${pattern(MONTHS)})(?:\\s+(${pattern(YEARS.keys())}))?(${SUFFIX})?(?![\\p{L}\\p{N}])`,
  'giu'
);

function clockSuffix(hour: number, minute: number): string {
  const value = minute || hour;
  if (value % 10) return ['da', 'de', 'de', 'te', 'te', 'te', 'da', 'de', 'de', 'da'][value % 10]!;
  return ({ 0: 'da', 10: 'da', 20: 'de', 30: 'da', 40: 'ta', 50: 'de' } as Record<number, string>)[
    value
  ]!;
}

/** Format explicit calendar dates and clock readings without resolving relative dates. */
export function formatDateTimeText(text: string): string {
  return text
    .replace(
      NUMERIC_DATE,
      (
        match,
        dayText: string,
        _separator: string,
        monthText: string,
        yearText: string,
        hour?: string,
        minute?: string
      ) => {
        const day = Number(dayText);
        const month = Number(monthText);
        const year = Number(yearText);
        if (
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > new Date(Date.UTC(year, month, 0)).getUTCDate()
        )
          return match;
        const monthName = MONTHS[month - 1]!;
        const title = monthName[0]!.toLocaleUpperCase('tr-TR') + monthName.slice(1);
        return `${day} ${title} ${year}${hour ? `, ${hour}:${minute}` : ''}`;
      }
    )
    .replace(
      DATE,
      (match, dayText: string, monthText: string, yearText?: string, suffix?: string) => {
        const day = lookup(DAYS, dayText);
        const month = MONTHS.indexOf(monthText.toLocaleLowerCase('tr-TR'));
        const year = yearText ? lookup(YEARS, yearText) : undefined;
        const lastDay = new Date(Date.UTC(year ?? 2000, month + 1, 0)).getUTCDate();
        if (day > lastDay) return match;
        const monthName = MONTHS[month]!;
        const title = monthName[0]!.toLocaleUpperCase('tr-TR') + monthName.slice(1);
        return `${day} ${title}${year ? ` ${year}` : ''}${suffix ? `'${suffix.replace(/['’]/g, '')}` : ''}`;
      }
    )
    .replace(
      TIME,
      (
        match,
        prefix: string,
        period: string | undefined,
        hourText: string,
        minuteText: string | undefined,
        minuteSuffix: string | undefined,
        hourSuffix: string | undefined,
        offset: number,
        source: string
      ) => {
        const suffix = minuteSuffix || hourSuffix;
        if (!suffix && FOLLOWING_NUMBER.test(source.slice(offset + match.length))) return match;
        let hour = lookup(HOURS, hourText);
        const minute = minuteText ? lookup(MINUTES, minuteText) : 0;
        const normalizedPeriod = period?.toLocaleLowerCase('tr-TR');
        if (
          (normalizedPeriod === 'akşam' || normalizedPeriod === 'öğleden sonra') &&
          hour > 0 &&
          hour < 12
        )
          hour += 12;
        if (normalizedPeriod === 'sabah' && hour >= 12) return match;
        return `${prefix}${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}${suffix ? `'${clockSuffix(hour, minute)}` : ''}`;
      }
    );
}
